import { useState, useEffect, useRef, memo, useCallback } from 'react';

import { IKnmLegalBasisParams } from 'APIPackage/KnmModule/interfaces/IKnmLegalBasisParams';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';

import { useLegalBasisFrequentlyUsed } from '../hooks/useLegalBasisFrequentlyUsed';
import { LegalBasesForm } from './LegalBasesForm';
import { LegalBasesFormRenderProps } from '../types/LegalBasesFormRenderProps';
import styles from './LegalBasesModal.module.scss';

const initialEmptySelectedActs: IKnmLegalBasisParams[] = [];

interface ILegalBasesModalProps {
  visible?: boolean;
  initialSelectedActs?: IKnmLegalBasisParams[];
  onApply?: (existLegalBases: IKnmLegalBasisParams[], notExistLegalBasisText?: string) => void;
  onClose: () => void;
}

export const LegalBasesModal = memo(
  ({ visible, initialSelectedActs = initialEmptySelectedActs, onApply, onClose }: ILegalBasesModalProps) => {
    const didVisibleRef = useRef(visible);

    if (visible) {
      didVisibleRef.current = true;
    }
    const {
      getLegalBasisFrequentlyUsed,
      saveLegalBasisFrequentlyUsed,
      legalBasisFrequentlyUsedDocuments,
    } = useLegalBasisFrequentlyUsed();

    useEffect(() => {
      getLegalBasisFrequentlyUsed(5);
    }, [getLegalBasisFrequentlyUsed]);

    const legalBasesFormRef = useRef<LegalBasesFormRenderProps['form']>();
    const resetFormRef = useRef<() => void>();
    const [selectedLegalBases, setSelectedLegalBases] = useState<IKnmLegalBasisParams[]>(initialSelectedActs);

    useEffect(() => {
      setSelectedLegalBases(initialSelectedActs);
    }, [initialSelectedActs]);

    const handleFormRender = useCallback((form: LegalBasesFormRenderProps['form'], resetForm: () => void) => {
      legalBasesFormRef.current = form;
      resetFormRef.current = resetForm;
    }, []);

    const handleSaveButtonClick = useCallback(async () => {
      const newFrequentlyUsedDocumentsIds: Array<string> = selectedLegalBases
        .filter(
          selectedLegalBasis =>
            selectedLegalBasis.externalId &&
            !initialSelectedActs.some(
              initialSelectedAct => initialSelectedAct.externalId === selectedLegalBasis.externalId,
            ),
        )
        .map(newSelectedLegalBasis => newSelectedLegalBasis.externalId || '');

      if (newFrequentlyUsedDocumentsIds.length) {
        await saveLegalBasisFrequentlyUsed(newFrequentlyUsedDocumentsIds);
      }
      const notExistLegalBasisText = legalBasesFormRef.current?.getState().values.notExistLegalBasisText;

      if (onApply) {
        onApply(selectedLegalBases, notExistLegalBasisText);
      }
      onClose();
      setSelectedLegalBases([]);
      resetFormRef.current && resetFormRef.current();
    }, [initialSelectedActs, onApply, onClose, saveLegalBasisFrequentlyUsed, selectedLegalBases]);

    const handleAfterClose = useCallback(() => {
      setSelectedLegalBases([]);
      resetFormRef.current && resetFormRef.current();
    }, []);

    return (
      <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
        <ModalHeader title="Выберите нормативно-правовые акты" />
        <ModalBody border="bottom">
          <div className={styles.BodyContent}>
            <LegalBasesForm
              active={didVisibleRef.current}
              selectedLegalBases={selectedLegalBases}
              frequentlyUsedDocuments={legalBasisFrequentlyUsedDocuments}
              setSelectedLegalBases={setSelectedLegalBases}
              onFormRender={handleFormRender}
            />
          </div>
        </ModalBody>
        <ModalActions>
          <Button text="Сохранить" id="saveButton" color="primary" size="medium" onClick={handleSaveButtonClick} />
          <Button text="Закрыть" size="medium" onClick={onClose} />
        </ModalActions>
      </Modal>
    );
  },
);
